import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Filter, Star } from 'lucide-react';
import { usePromotional } from '../contexts/PromotionalContext';
import { buildUdemyUrl } from '../utils/udemyLinks';
import CouponBanner from '../components/ui/CouponBanner';
import PromotionalBanner from '../components/ui/PromotionalBanner';
import { AI_COURSE_IMAGES } from '../config/courseImages';

const CoursesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { config, isActive } = usePromotional();
  const [searchParams] = useSearchParams();

  // Read search parameter from URL on component mount
  useEffect(() => {
    const urlSearchQuery = searchParams.get('search');
    if (urlSearchQuery) {
      setSearchTerm(urlSearchQuery);
    }
  }, [searchParams]);

  const categories = [
    { id: 'all', name: 'All Courses' },
    { id: 'ai-testing', name: 'AI Testing' },
    { id: 'genai', name: 'Generative AI' },
    { id: 'automation', name: 'Test Automation' },
    { id: 'ai-development', name: 'AI Development' },
  ];

  const courses = [
    {
      id: 8,
      title: 'Red Teaming AI Apps with Promptfoo: RAG, Chatbots & Agents',
      description: 'Learn practical AI security testing with Promptfoo. Test real RAG apps, chatbots, and AI agents for jailbreaks, prompt injection, PII leakage, hallucination, excessive agency, policy violations, and knowledge-base poisoning.',
      image: AI_COURSE_IMAGES['ai-red-teaming-promptfoo-rag-chatbots-agents'],
      price: 'Enroll on Udemy',
      originalPrice: '',
      rating: 5.0,
      students: 47,
      category: 'ai-testing',
      duration: '8 hours',
      level: 'Beginner',
      udemyUrl: 'https://www.udemy.com/course/ai-red-teaming-promptfoo-rag-chatbots-agents/',
    },
    {
      id: 0,
      title: 'ZERO Code AI Tests: Automate Software Tests in Plain English',
      description: 'Automate software tests without writing a single line of code using AI. Describe tests in plain English and let AI generate, execute, and maintain them for you.',
      image: AI_COURSE_IMAGES['zero-code-ai-testing'],
      price: 'Enroll on Udemy',
      originalPrice: '',
      rating: 5.0,
      students: 1000,
      category: 'ai-testing',
      duration: '10 hours',
      level: 'Beginner',
      udemyUrl: 'https://www.udemy.com/course/zero-code-ai-testing/',
    },
    {
      id: 1,
      title: 'Build Agentic AI & Multi-Agent System for Software Testing',
      description: 'Learn Building and Testing AI Agent, ChatBot, RAG with LangChain and LangSmith using Ollama and Local LLMs and RAGAs.',
      image: AI_COURSE_IMAGES['genai-multi-agent-software-testing'],
      price: 'Enroll on Udemy',
      originalPrice: '',
      rating: 4.8,
      students: 3000,
      category: 'ai-testing',
      duration: '18 hours',
      level: 'Advanced',
      udemyUrl: 'https://www.udemy.com/course/genai-multi-agent-software-testing/',
    },
    {
      id: 2,
      title: 'E2E Testing ChatBot, AI Agent, RAG, MCP Server with DeepEval',
      description: 'AI-powered applications are reshaping the software landscape. Build a complete end-to-end testing strategy for ChatBots, AI Agents, RAG pipelines, and MCP Servers using DeepEval — grounded in a real-world e-commerce AI chatbot application.',
      image: AI_COURSE_IMAGES['ai-testing-deepeval-chatbot-rag-agent-mcp'],
      price: 'Enroll on Udemy',
      originalPrice: '',
      rating: 4.9,
      students: 500,
      category: 'ai-testing',
      duration: '14 hours',
      level: 'Intermediate',
      udemyUrl: 'https://www.udemy.com/course/ai-testing-deepeval-chatbot-rag-agent-mcp/',
    },
    {
      id: 7,
      title: 'AI Agents, RAG & LLM Evals for Beginners: DeepEval & RAGAS',
      description: 'Learn to test AI applications using specialized frameworks like DeepEval, RAGAs, HuggingFace Evaluate, Confident AI along with using Ollama with Local LLMs for comprehensive AI validation. Testing AI Agents, MCP Server, ChatBots, LLMs.',
      image: AI_COURSE_IMAGES['ai-testing-deepeval-ragas-ollama'],
      price: 'Enroll on Udemy',
      originalPrice: '',
      rating: 4.9,
      students: 1500,
      category: 'ai-testing',
      duration: '12 hours',
      level: 'Intermediate',
      udemyUrl: 'https://www.udemy.com/course/ai-testing-deepeval-ragas-ollama/',
    },
    {
      id: 3,
      title: 'LLM Fine-Tuning for Beginners: HuggingFace & Unsloth',
      description: 'Understand Transformer models and learn how to Test, Evaluate, and Train them with HuggingFace NLP library.',
      image: AI_COURSE_IMAGES['ai-with-huggingface'],
      price: 'Enroll on Udemy',
      originalPrice: '',
      rating: 4.9,
      students: 1200,
      category: 'ai-development',
      duration: '20 hours',
      level: 'Expert',
      udemyUrl: 'https://www.udemy.com/course/ai-with-huggingface/',
    },
    {
      id: 4,
      title: 'LangChain for Beginners : Building AI Agents, RAG & ChatBots',
      description: 'Master building and testing AI agents, chatbots, and RAG systems using AutoGen, LangChain with Ollama and local LLMs. See how you can empower existing test automation workflow with Modern AI Agents and simplify automation in every aspect.',
      image: AI_COURSE_IMAGES['build-ai-agent-chatbot-rag-langchain-local-llm'],
      price: 'Enroll on Udemy',
      originalPrice: '',
      rating: 4.8,
      students: 1200,
      category: 'ai-development',
      duration: '15 hours',
      level: 'Advanced',
      udemyUrl: 'https://www.udemy.com/course/build-ai-agent-chatbot-rag-langchain-local-llm/',
    },
    {
      id: 5,
      title: 'Using Gen AI & AI Agent in Software Automation Testing',
      description: 'Start your AI testing journey with fundamentals of GenAI, prompt engineering, AI Agents, MCP server usage, making Selenium and Playwright test automation smarter with AI.',
      image: AI_COURSE_IMAGES['generative-ai-in-software-automation-testing'],
      price: 'Enroll on Udemy',
      originalPrice: '',
      rating: 4.7,
      students: 8000,
      category: 'genai',
      duration: '8 hours',
      level: 'Beginner',
      udemyUrl: 'https://www.udemy.com/course/generative-ai-in-software-automation-testing/',
    },
    {
      id: 6,
      title: 'AI-Driven Test Automation: Playwright, Selenium, LLMs & More',
      description: 'Build intelligent, resilient test automation frameworks with AI-powered self-healing locators. Master Selenium & Playwright with local LLMs via Ollama for zero flaky tests and reduced maintenance.',
      image: AI_COURSE_IMAGES['ai-test-automation-selenium-playwright-llms'],
      price: 'Enroll on Udemy',
      originalPrice: '',
      rating: 5.0,
      students: 82,
      category: 'automation',
      duration: '15 hours',
      level: 'Intermediate',
      udemyUrl: 'https://www.udemy.com/course/ai-test-automation-selenium-playwright-llms/',
    },
  ];

  const filteredCourses = courses.filter((course) => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || course.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
          AI & <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">GenAI Testing</span> Courses
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          Master the future of software testing with our comprehensive AI and Generative AI courses.
          Learn cutting-edge techniques for automated testing, AI agent development, and quality assurance.
        </p>
      </div>

      {/* Promotional Banner */}
      <div className="mb-8">
        <PromotionalBanner 
          variant="inline"
          className="max-w-4xl mx-auto"
        />
      </div>

      {/* Coupon Banner */}
      {config.isEnabled && isActive && (
        <div className="flex justify-center mb-8">
          <div className="max-w-2xl w-full px-4">
            <CouponBanner
              code={config.couponCode}
              description={config.description}
              variant="banner"
              className="shadow-lg hover:shadow-xl transition-shadow duration-300"
            />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-8 space-y-4 md:space-y-0 md:flex md:items-center md:justify-between">
        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white shadow-sm hover:border-green-300 transition-colors"
          />
        </div>

        {/* Category Filter */}
        <div className="flex items-center space-x-4">
          <Filter className="h-5 w-5 text-gray-400" />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white shadow-sm hover:border-green-300 transition-colors"
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-6">
        <p className="text-gray-600">
          Showing {filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Courses Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredCourses.map((course) => (
          <div
            key={course.id}
            className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-xl hover:-translate-y-2 hover:scale-105 transition-all duration-300 transform cursor-pointer group"
          >
            <img
              src={course.image}
              alt={course.title}
              loading="lazy"
              className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
            />
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                  course.level === 'Beginner' ? 'text-green-700 bg-green-100' :
                  course.level === 'Intermediate' ? 'text-blue-700 bg-blue-100' :
                  course.level === 'Advanced' ? 'text-purple-700 bg-purple-100' :
                  course.level === 'Expert' ? 'text-orange-700 bg-orange-100' :
                  'text-gray-700 bg-gray-100'
                }`}>
                  {course.level}
                </span>
                <span className="text-xs text-gray-500">{course.duration}</span>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                {course.title}
              </h3>
              
              <p className="text-gray-600 mb-4 line-clamp-2">
                {course.description}
              </p>
              
              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <div className="flex items-center">
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400 mr-1 star-rating-glow" />
                  {course.rating}
                </div>
                <div>{course.students > 0 ? `${course.students.toLocaleString()}+ students` : 'New Course'}</div>
              </div>

              {/* Coupon Code Display */}
              {config.isEnabled && isActive && (
                <div className="mb-4">
                  <CouponBanner
                    code={config.couponCode}
                    description={config.description}
                    variant="card"
                    showCopy={false}
                    className="text-xs"
                  />
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                    {course.price}
                  </span>
                  {course.originalPrice && (
                    <span className="text-sm text-gray-400 line-through">
                      {course.originalPrice}
                    </span>
                  )}
                </div>
                {course.udemyUrl ? (
                  <a
                    href={buildUdemyUrl(course.udemyUrl, config, isActive)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all transform hover:scale-105"
                  >
                    View on Udemy
                  </a>
                ) : (
                  <Link
                    to={`/courses/${course.id}`}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all transform hover:scale-105"
                  >
                    View Course
                  </Link>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredCourses.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Search className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No courses found
          </h3>
          <p className="text-gray-600">
            Try adjusting your search terms or filters to find what you're looking for.
          </p>
        </div>
      )}
    </div>
  );
};

export default CoursesPage;
